import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2, CheckCircle2, Clock, XCircle, Building2 } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const ICON = { pending: Clock, approved: CheckCircle2, rejected: XCircle, converted: Building2 };
const COLOR = { pending: "text-amber-600 bg-amber-50", approved: "text-emerald-600 bg-emerald-50", rejected: "text-rose-600 bg-rose-50", converted: "text-sky-600 bg-sky-50" };
const DESC = {
    pending: "Başvurunuz ekibimiz tarafından inceleniyor. Sonuç e-posta ile bildirilecek.",
    approved: "Başvurunuz onaylandı. Extranet hesabınız hazırlanıyor; giriş bilgileriniz e-posta ile gönderilecek.",
    rejected: "Başvurunuz şu an için onaylanamadı.",
    converted: "Extranet hesabınız açıldı. Giriş bilgileriniz e-posta adresinize gönderildi.",
};
const fmt = (s) => (s ? new Date(s).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const INPUT = "w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white outline-none focus:border-emerald-500";

function Result({ r }) {
    const Icon = ICON[r.status] || Clock;
    return (
        <div data-testid="track-result" className="mt-6 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="flex items-center gap-3">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${COLOR[r.status]}`}><Icon className="h-6 w-6" /></div>
                <div>
                    <div className="text-xs text-slate-500 font-mono">{r.number} · {r.property_name}</div>
                    <div data-testid="track-status" className="text-lg font-bold text-slate-900 dark:text-white">{r.status_label}</div>
                </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-3">{DESC[r.status]}</p>
            {r.reject_reason && <p className="text-sm mt-2 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300"><b>Gerekçe:</b> {r.reject_reason}</p>}
            <ol className="mt-5 border-l-2 border-slate-200 dark:border-slate-700 pl-4 space-y-3">
                {r.history.map((h, i) => (
                    <li key={i} className="relative">
                        <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full bg-emerald-500 ring-4 ring-white dark:ring-slate-900" />
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{h.label}</div>
                        <div className="text-xs text-slate-500">{fmt(h.at)}</div>
                    </li>
                ))}
            </ol>
        </div>
    );
}

export default function ApplicationTrackPage() {
    const [params] = useSearchParams();
    const [no, setNo] = useState(params.get("no") || "");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => { document.title = "Başvuru Takip · msonlinetravel"; }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(""); setRes(null);
        try { const r = await api.get("/hotel-applications/track", { params: { no, email } }); setRes(r.data); }
        catch (e2) { setErr(e2.response?.data?.detail || "Sorgulama başarısız"); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
            <Navbar />
            <main className="max-w-xl mx-auto px-4 py-16">
                <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">Başvuru Takip</h1>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">Tesis başvurunuzun durumunu, size e-posta ile gönderilen başvuru numarası ve başvuruda kullandığınız e-posta adresiyle sorgulayabilirsiniz.</p>
                <form onSubmit={submit} className="mt-6 space-y-3" data-testid="track-form">
                    <input data-testid="track-no" value={no} onChange={(e) => setNo(e.target.value.toUpperCase())} placeholder="Başvuru No (ör. BSV-1007)" className={INPUT} required />
                    <input data-testid="track-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-posta adresiniz" className={INPUT} required />
                    <button data-testid="track-submit" disabled={busy} className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Sorgula
                    </button>
                </form>
                {err && <div data-testid="track-error" className="mt-4 p-3 rounded-xl bg-rose-50 text-rose-700 text-sm">{err}</div>}
                {res && <Result r={res} />}
            </main>
            <Footer />
        </div>
    );
}
